import { z } from 'zod';
import { isMockServices } from 'src/utils';
import mock from 'src/services/utils/mock';
import { QueryFunction } from '@tanstack/react-query';
import { GetUserTasksResponse } from './types';
import { mockGetUserTasksResponse } from './mockData';
import { getCompatibleAuthTokenHttp } from '../utils/enhancedAuthTokenHttp';
import { userTasksKeys } from 'src/queries/tasks/useGetUserTasks';

/** Accept API nulls and normalize to `undefined` for optional UserTask fields. */
const optionalString = z
  .string()
  .nullish()
  .transform((value) => value ?? undefined);

const optionalNumber = z
  .number()
  .nullish()
  .transform((value) => value ?? undefined);

const UserTaskDocumentSchema = z
  .object({
    versionIdentifier: z.string().nullish(),
    scope: z.string().nullish(),
    documentTypeId: optionalString,
    documentType: z
      .object({
        id: z.string(),
        name: z.string(),
        displayName: z.string(),
      })
      .passthrough(),
    metadata: z
      .object({
        external_document_filename: optionalString,
      })
      .passthrough()
      .optional(),
  })
  .passthrough();

const UserTaskSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    status: z.string(),
    priority: z.string(),
    taskType: optionalString,
    documentId: optionalString,
    isMapped: z.boolean().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    isDeleted: z.boolean(),
    document: UserTaskDocumentSchema.nullish().transform(
      (value) => value ?? undefined,
    ),
    extractedData: z
      .object({
        id: z.string(),
        fieldTypeId: optionalString,
        fieldType: z
          .object({
            id: optionalString,
            displayName: z.string(),
            documentTypeId: z.string(),
          })
          .passthrough(),
        initialValue: optionalString,
        fieldConfidence: optionalNumber,
      })
      .passthrough()
      .nullish()
      .transform((value) => value ?? undefined),
  })
  .passthrough();

export const GetUserTasksResponseSchema = z.object({
  success: z.boolean(),
  tasks: z.array(UserTaskSchema),
  nextCursor: optionalString,
});

const getUserTasks: QueryFunction<
  GetUserTasksResponse,
  ReturnType<typeof userTasksKeys.list>
> = async (args) => {
  if (isMockServices()) return await mock(mockGetUserTasksResponse);

  const params = args.queryKey[1];
  const queryParams = new URLSearchParams();

  if (params.loanId) queryParams.append('loanId', params.loanId);
  if (params.status) queryParams.append('status', params.status);
  if (params.priority) queryParams.append('priority', params.priority);
  if (params.assignedToUserId)
    queryParams.append('assignedToUserId', params.assignedToUserId);
  if (params.cursor) queryParams.append('cursor', params.cursor);
  if (params.limit) queryParams.append('limit', params.limit.toString());

  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<unknown>(
    `/userTasks?${queryParams.toString()}`,
    {
      method: 'GET',
      paginate: false,
    },
  );

  if (response.data) {
    return GetUserTasksResponseSchema.parse(response.data);
  }
  throw new Error('Failed to fetch user tasks');
};

export default getUserTasks;
